import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../store/auth-context';
import { filterAssignableRoles } from '../../utils/roleUtils';
import { Button } from '../ui/button';
import { CalendarClock, Lightbulb, Plus, Users } from 'lucide-react';
import { formatShortCalendarDate } from '../../utils/dateUtils';
import { useToast } from '../../hooks/use-toast';
import {
    getAvailableRoles,
    getMyDepartmentUsers,
    sendBulkInvites,
    updateUserRole,
    type RoleOption,
} from '../../services/api/onboardingService';
import { deleteInvitation } from '../../services/apiService';
import type { FacilityUser } from '../../services/api/facilityService';
import UserInvitationsTable from '../../components/facilities/tables/UserInvitationsTable';
import DeptPermissionTemplatesSection from '../facilities/DeptPermissionTemplatesSection';
import RIGHTS_KEYS from '../../constants/rights-keys';
import {
    facilityUsersToRows,
    type UserRow,
} from '../../components/facilities/tables/userManagementTypes';
import {
    parseEmails,
    shouldHandlePastedInviteText,
} from '../facilities/utils/inviteUserUtils';
import { useOnboarding } from '../../store/onboarding-context';

interface DraftEntry {
    _id: string;
    email: string;
    roleId: string;
}

export default function UsersStep() {
    const { department, currentUserId } = useOnboarding();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const authContext = useContext(AuthContext);
    const licenses = department?.licenses ?? 0;
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [draftEntries, setDraftEntries] = useState<DraftEntry[]>([]);
    const [existingUsers, setExistingUsers] = useState<FacilityUser[]>([]);
    const [pasteValue, setPasteValue] = useState('');

    const fetchUsers = async () => {
        try {
            const users = await getMyDepartmentUsers();
            if (Array.isArray(users)) setExistingUsers(users);
        } catch {
            toast({
                title: t('onboarding.users.toasts.loadError'),
                variant: 'destructive',
            });
        }
    };

    useEffect(() => {
        const loadUsersStepData = async () => {
            try {
                const [availableRoles, departmentUsers] = await Promise.all([
                    getAvailableRoles(),
                    getMyDepartmentUsers(),
                ]);

                if (Array.isArray(availableRoles)) {
                    setRoles(
                        filterAssignableRoles(availableRoles, authContext)
                    );
                }
                if (Array.isArray(departmentUsers)) {
                    setExistingUsers(departmentUsers);
                }
            } catch {
                toast({
                    title: t('onboarding.common.error'),
                    description: t('onboarding.users.toasts.loadError'),
                    variant: 'destructive',
                });
            }
        };

        loadUsersStepData();
    }, [toast, authContext]);

    const totalUsed = draftEntries.length + existingUsers.length;
    const availableLicenseSlots =
        licenses > 0 ? Math.max(0, licenses - totalUsed) : null;
    const hasAvailableLicenseSlots =
        availableLicenseSlots === null || availableLicenseSlots > 0;

    const addEmails = useCallback((rawValue: string) => {
        const { valid, invalid } = parseEmails(rawValue);

        if (invalid.length > 0) {
            toast({
                title: t('onboarding.users.toasts.invalidEmails', { count: invalid.length }),
                description: t('onboarding.users.toasts.invalidEmailsDesc'),
                variant: 'destructive',
            });
        }

        if (valid.length === 0) {
            return { addedCount: 0, invalidCount: invalid.length };
        }

        const existingEmails = new Set([
            ...draftEntries.map((e) => e.email),
            ...existingUsers.map((u) => u.email.toLowerCase()),
        ]);

        let newEntries = valid
            .filter((email) => !existingEmails.has(email))
            .map((email) => ({
                _id: `draft-${email}`,
                email,
                roleId: '',
            }));

        const skippedCount =
            availableLicenseSlots === null
                ? 0
                : Math.max(0, newEntries.length - availableLicenseSlots);

        if (availableLicenseSlots !== null) {
            newEntries = newEntries.slice(0, availableLicenseSlots);
        }

        if (skippedCount > 0) {
            toast({
                title: t('onboarding.users.toasts.licenseLimit'),
                description: t('onboarding.users.toasts.licenseLimitDesc', { added: newEntries.length, skipped: skippedCount }),
            });
        }

        if (newEntries.length > 0) {
            setDraftEntries((prev) => [...prev, ...newEntries]);
        }
        return { addedCount: newEntries.length, invalidCount: invalid.length };
    }, [availableLicenseSlots, draftEntries, existingUsers, toast, t]);

    const addEmailsFromInput = useCallback(() => {
        const result = addEmails(pasteValue);

        if (result.addedCount > 0) setPasteValue('');

        return result;
    }, [addEmails, pasteValue]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            addEmailsFromInput();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const pasted = e.clipboardData.getData('text');
        if (shouldHandlePastedInviteText(pasted)) {
            e.preventDefault();
            const { addedCount } = addEmails(pasted);

            if (addedCount > 0) {
                toast({ title: t('onboarding.users.toasts.usersAdded', { count: addedCount }) });
            }
        }
    };

    const handleNext = async () => {
        if (pasteValue.trim()) {
            const { addedCount, invalidCount } = addEmailsFromInput();
            if (addedCount > 0 || invalidCount > 0) return;
        }

        if (draftEntries.length > 0) {
            try {
                await sendBulkInvites(
                    draftEntries.map((e) => ({
                        email: e.email,
                        actorId: e.roleId || undefined,
                    }))
                );
            } catch {
                toast({
                    title: t('onboarding.common.error'),
                    description: t('onboarding.users.toasts.inviteError'),
                    variant: 'destructive',
                });
                return;
            }
        }

        navigate('/wizard/scanners');
    };

    // Combine drafts and existing users into UserRow[]
    const draftRows: UserRow[] = draftEntries.map((e) => ({
        _id: e._id,
        email: e.email,
        role: roles.find((r) => r._id === e.roleId)?.role || t('onboarding.users.pending'),
        roleId: e.roleId,
        status: 'draft' as const,
        removable: true,
    }));

    const existingRows = facilityUsersToRows(existingUsers).map((row) =>
        row._id === currentUserId
            ? {
                  ...row,
                  isCurrentUser: true,
                  removable: false,
                  editable: false,
                  selectable: false,
              }
            : row
    );
    const allRows: UserRow[] = [...draftRows, ...existingRows];

    const handleRoleChange = async (userId: string, roleId: string) => {
        const isDraftUser = draftEntries.some((entry) => entry._id === userId);
        const roleName = roles.find((role) => role._id === roleId)?.role;

        if (isDraftUser) {
            setDraftEntries((prev) =>
                prev.map((entry) =>
                    entry._id === userId ? { ...entry, roleId } : entry
                )
            );
            return;
        }

        setExistingUsers((prev) =>
            prev.map((user) =>
                user._id === userId
                    ? { ...user, role: roleName || user.role, roleId }
                    : user
            )
        );

        try {
            await updateUserRole(userId, roleId);
        } catch {
            toast({
                title: t('onboarding.common.error'),
                description: t('onboarding.users.toasts.roleUpdateError'),
                variant: 'destructive',
            });
            fetchUsers();
        }
    };

    const handleRemove = async (userId: string) => {
        const isDraftUser = draftEntries.some((entry) => entry._id === userId);

        if (isDraftUser) {
            setDraftEntries((prev) => prev.filter((e) => e._id !== userId));
            return;
        }

        try {
            await deleteInvitation(userId);
            setExistingUsers((prev) => prev.filter((u) => u._id !== userId));
        } catch {
            toast({
                title: t('onboarding.users.toasts.deleteFailed'),
                description: t('onboarding.users.toasts.deleteFailedDesc'),
                variant: 'destructive',
            });
        }
    };

    const handleBulkRoleAssign = async (userIds: string[], roleId: string) => {
        const targetIds = userIds.filter((id) => id !== currentUserId);
        const draftIds = new Set(draftEntries.map((entry) => entry._id));
        const draftUserIds = targetIds.filter((userId) => draftIds.has(userId));
        const existingUserIds = targetIds.filter(
            (userId) => !draftIds.has(userId)
        );
        const roleName = roles.find((role) => role._id === roleId)?.role;

        setDraftEntries((prev) =>
            prev.map((entry) =>
                draftUserIds.includes(entry._id)
                    ? { ...entry, roleId }
                    : entry
            )
        );

        if (existingUserIds.length === 0) return;

        setExistingUsers((prev) =>
            prev.map((user) =>
                existingUserIds.includes(user._id)
                    ? { ...user, role: roleName || user.role, roleId }
                    : user
            )
        );

        try {
            await Promise.all(
                existingUserIds.map((userId) => updateUserRole(userId, roleId))
            );
        } catch {
            toast({
                title: t('onboarding.common.error'),
                description: t('onboarding.users.toasts.bulkRoleError'),
                variant: 'destructive',
            });
            fetchUsers();
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">
                        {t('onboarding.users.title')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('onboarding.users.description')}
                    </p>
                </div>
                {licenses > 0 && (
                    <div className="flex items-center gap-2 rounded-lg border border-subtle bg-muted px-3 py-2">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                        <span className="text-xs tabular-nums text-foreground">
                            {totalUsed}
                            <span className="text-muted-foreground"> / {licenses}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">{t('onboarding.users.licenses')}</span>
                    </div>
                )}
            </div>

            {allRows.length > 0 && department?.start_date && (
                <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-200">
                    <CalendarClock
                        className="h-4 w-4 shrink-0 text-emerald-400"
                        aria-hidden="true"
                    />
                    <span>
                        {t(
                            department?.isTrial === false
                                ? 'onboarding.users.invitesSentOnGoLive'
                                : 'onboarding.users.invitesSentOn',
                            { date: formatShortCalendarDate(department.start_date) }
                        )}
                    </span>
                </div>
            )}

            <div className="flex flex-col gap-6 xl:flex-row">
                {/* Left — input panel */}
                <div className="w-full shrink-0 space-y-4 xl:w-80">
                    <div>
                        <textarea
                            className="w-full rounded-md border border-subtle bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                            rows={6}
                            placeholder={t('onboarding.users.placeholder')}
                            value={pasteValue}
                            onChange={(e) => setPasteValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                        />

                        <div className="mt-3">
                            <Button
                                size="sm"
                                className="w-full"
                                onClick={addEmailsFromInput}
                                disabled={
                                    !pasteValue.trim() ||
                                    !hasAvailableLicenseSlots
                                }
                            >
                                <Plus className="mr-1.5 h-3 w-3" aria-hidden="true" />
                                {t('onboarding.users.addUsers')}
                            </Button>
                        </div>
                        {availableLicenseSlots !== null && (
                            <p className="mt-2 text-center text-xs text-muted-foreground">
                                {availableLicenseSlots > 0
                                    ? t('onboarding.users.slotsAvailable', { count: availableLicenseSlots })
                                    : t('onboarding.users.allSlotsFilled')}
                            </p>
                        )}
                    </div>
                </div>

                {/* Right — users table */}
                <div className="min-w-0 flex-1 space-y-2">
                    {allRows.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Lightbulb className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            <span>{t('onboarding.users.bulkHint')}</span>
                        </div>
                    )}
                    <div className="max-h-96 overflow-y-auto rounded-md border border-subtle">
                        <UserInvitationsTable
                            users={allRows}
                            roles={roles}
                            tableMode="onboarding"
                            onRoleChange={handleRoleChange}
                            onRemove={handleRemove}
                            onBulkRoleAssign={handleBulkRoleAssign}
                            emptyMessage={t('onboarding.users.emptyMessage')}
                        />
                    </div>
                </div>
            </div>

            <DeptPermissionTemplatesSection
                departmentId={department?._id ?? ''}
                viewer="siteAdmin"
                users={existingUsers}
                canEditTemplates={authContext.orRights([
                    RIGHTS_KEYS.manageEmployees,
                ])}
                showDefaultHint
                onUsersChanged={fetchUsers}
            />

            {/* Navigation */}
            <div className="flex justify-between pt-4">
                <Button
                    variant="outline"
                    onClick={() => navigate('/wizard/business-info')}
                >
                    {t('onboarding.common.back')}
                </Button>
                <Button onClick={handleNext}>
                    {t('onboarding.common.saveAndContinue')}
                </Button>
            </div>
        </div>
    );
}
